import React, { ChangeEvent, useState, useCallback } from 'react';
import {
    Step, StepLabel, Stepper, Box, Button, MenuItem, TextField,
    TableHead, TableRow, TableCell, TableBody, Typography,
    Chip, Tooltip, IconButton, Alert, FormControlLabel, Checkbox
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CloseConfirmationDialog, LoadingBackDrop, StepperDialog, StyledTable, SelectFileIcon } from '@lp/lp-ui';
import {
    parseCsv, autoMapFields, mapRowsToUsers, validateBulkUsers, validateInviteRows,
    BulkUserRow, FieldMapping, RowValidationError, BULK_USER_FIELDS, INVITE_USER_FIELDS, CsvParseResult
} from '../../csvParser';
import { BulkImportUsersResultModel, OrganizationMemberModel } from '../../typings';
import { getSubmittedIndexes, canRetryImport, buildCsvContent, BulkUsersMode } from './bulkImportHelpers';
import identityService from '../../services/IdentityService';
import { getBodyErrors } from '../../services/ApiService';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { usersFilter, paginationSelected } from '../../app/usersSlice';
import { operationStarted, subscribeToBulkProgress } from '../../app/bulkProgressSlice';
import BulkImportApplicationsPanel from './BulkImportApplicationsPanel';
import BulkImportRolesPanel from './BulkImportRolesPanel';
import BulkImportOrganizationsPanel from './BulkImportOrganizationsPanel';

interface BulkImportUsersDialogProps {
    open: boolean;
    handleClose: () => void;
    mode?: BulkUsersMode;
}

const steps = ['Upload File', 'Map Fields', 'Review', 'Applications', 'Roles', 'Organizations', 'Results'];

const BulkImportUsersDialog: React.FC<BulkImportUsersDialogProps> = ({ open, handleClose, mode = 'import' }) => {
    const isInvite = mode === 'invite';
    const fields = isInvite ? INVITE_USER_FIELDS : BULK_USER_FIELDS;
    const validateRows = isInvite ? validateInviteRows : validateBulkUsers;
    const title = isInvite ? 'Invite Users' : 'Import Users';
    const dispatch = useAppDispatch();
    const { pagination, organizationId } = useAppSelector(store => store.users);
    const [activeStep, setActiveStep] = useState(0);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Step 1 state
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [csvData, setCsvData] = useState<CsvParseResult | null>(null);
    const [fileError, setFileError] = useState('');

    // Step 2 state
    const [mappings, setMappings] = useState<FieldMapping[]>(fields);
    const [appliedMappings, setAppliedMappings] = useState<FieldMapping[]>([]);

    // Step 3 state
    const [users, setUsers] = useState<BulkUserRow[]>([]);
    const [validationErrors, setValidationErrors] = useState<RowValidationError[]>([]);
    const [removedRows, setRemovedRows] = useState<Set<number>>(new Set());

    // Import options
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

    // Step 4 (Applications) state
    const [userClientIds, setUserClientIds] = useState<number[]>([]);

    // Step 5 (Roles) state
    const [roleIds, setRoleIds] = useState<number[]>([]);

    // Step 6 (Organizations) state
    const [userOrganizations, setUserOrganizations] = useState<OrganizationMemberModel[]>([]);

    // Step 7 (Results) state
    const [importResult, setImportResult] = useState<BulkImportUsersResultModel | null>(null);
    const [importError, setImportError] = useState('');
    // Original `users` indices of the rows sent in the last import request, in
    // submission order: result rowNumber N maps to submittedIndexes[N - 1].
    const [submittedIndexes, setSubmittedIndexes] = useState<number[]>([]);

    const resetState = useCallback(() => {
        setActiveStep(0);
        setFile(null);
        setFileName('');
        setCsvData(null);
        setFileError('');
        setMappings(fields);
        setAppliedMappings([]);
        setUsers([]);
        setValidationErrors([]);
        setRemovedRows(new Set());
        setSendWelcomeEmail(true);
        setTwoFactorEnabled(false);
        setUserClientIds([]);
        setRoleIds([]);
        setUserOrganizations([]);
        setImportResult(null);
        setImportError('');
        setSubmittedIndexes([]);
        setLoading(false);
    }, [fields]);

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const uploadedFile = e.target.files[0];
        setFileError('');
        setFileName(uploadedFile.name);
        setFile(uploadedFile);

        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target?.result as string;
            if (!text || text.trim() === '') {
                setFileError('File is empty');
                setCsvData(null);
                setFile(null);
                return;
            }
            const result = parseCsv(text);
            if (result.headers.length === 0) {
                setFileError('No headers found in CSV file');
                setCsvData(null);
                setFile(null);
                return;
            }
            setCsvData(result);
            setMappings(autoMapFields(result.headers, fields));
            setAppliedMappings([]);
            setUsers([]);
            setValidationErrors([]);
            setRemovedRows(new Set());
        };
        reader.readAsText(uploadedFile);
        e.target.value = '';
    };

    const handleMappingChange = (systemField: string, csvColumn: string) => {
        setMappings(prev => prev.map(m =>
            m.systemField === systemField ? { ...m, csvColumn } : m
        ));
    };

    const handleRemoveRow = (rowIndex: number) => {
        setRemovedRows(prev => new Set(prev).add(rowIndex));
    };

    const handleRemoveFailedRow = (rowNumber: number) => {
        setRemovedRows(prev => new Set(prev).add(submittedIndexes[rowNumber - 1]));
    };

    const getActiveUsers = (): BulkUserRow[] => {
        return users.filter((_, i) => !removedRows.has(i));
    };

    const getActiveValidationErrors = (): RowValidationError[] => {
        return validationErrors.filter(e => !removedRows.has(e.rowIndex));
    };

    const handleNext = () => {
        if (activeStep === 0) {
            if (!canProceedStep0) return;
            setActiveStep(1);
        } else if (activeStep === 1) {
            if (!canProceedStep1 || !csvData) return;

            const mappingsChanged = JSON.stringify(mappings) !== JSON.stringify(appliedMappings);
            if (mappingsChanged || users.length === 0) {
                const mapped = mapRowsToUsers(csvData.rows, csvData.headers, mappings);
                const errors = validateRows(mapped);
                setUsers(mapped);
                setValidationErrors(errors);
                setAppliedMappings(mappings);
                if (users.length === 0) {
                    setRemovedRows(new Set());
                }
            }
            setActiveStep(2);
        } else if (activeStep === 2) {
            if (!canProceedStep2) return;
            setActiveStep(3);
        } else if (activeStep === 3) {
            setActiveStep(4);
        } else if (activeStep === 4) {
            setActiveStep(5);
        }
    };

    const handleBack = () => {
        if (activeStep === 6 && importResult && importResult.failureCount > 0) {
            setImportResult(null);
            setImportError('');
            setActiveStep(5);
        } else if (activeStep > 0 && activeStep < 6) {
            setActiveStep(activeStep - 1);
        }
    };

    const buildCsvFromActiveUsers = (): File =>
        new File([buildCsvContent(getActiveUsers(), mode)], fileName || (isInvite ? 'invites.csv' : 'import.csv'), { type: 'text/csv' });

    const handleSave = async () => {
        if (!file) return;
        const activeUsers = getActiveUsers();
        if (activeUsers.length === 0) return;

        setLoading(true);
        setImportError('');
        // Freeze the submission-order mapping so failed rowNumbers can be traced
        // back to `users` indices even after further removals on the Results step
        setSubmittedIndexes(getSubmittedIndexes(users.length, removedRows));

        try {
            const csvFile = buildCsvFromActiveUsers();
            const orgPayload = userOrganizations.map(org => ({
                organizationId: org.organizationId,
                roles: org.roles.map(r => r.id)
            }));
            const response = isInvite
                ? await identityService.bulkInviteUsers(csvFile, twoFactorEnabled, userClientIds, roleIds, orgPayload)
                : await identityService.importUsers(csvFile, sendWelcomeEmail, userClientIds, roleIds, orgPayload);
            setImportResult(response.data);
            if (response.data?.operationId) {
                dispatch(operationStarted({ operationId: response.data.operationId, total: activeUsers.length, label: title }));
                dispatch(subscribeToBulkProgress(response.data.operationId));
            }
            setActiveStep(6);
        } catch (err: any) {
            if (err.status === 400 && err.json) {
                try {
                    const body = await err.json();
                    if (body.data?.results) {
                        setImportResult(body.data);
                        setActiveStep(6);
                        return;
                    }
                } catch { /* body not parseable, fall through */ }
            }
            const errors = await getBodyErrors(err);
            setImportError(errors.map(e => e.detail).join('; '));
        } finally {
            setLoading(false);
        }
    };

    const handleCloseClicked = () => {
        if (activeStep === 6) {
            // On results step, close directly and refresh list
            handleCloseAfterImport();
            return;
        }
        if (csvData || fileName) {
            setCloseDialogOpen(true);
        } else {
            resetState();
            handleClose();
        }
    };

    const handleCloseConfirmed = () => {
        setCloseDialogOpen(false);
        resetState();
        handleClose();
    };

    const handleCloseAfterImport = () => {
        if (importResult && importResult.successCount > 0) {
            const pgn = { ...pagination, organizationId, shouldApplySearch: true };
            dispatch(paginationSelected(pgn));
            dispatch(usersFilter(pgn));
        }
        resetState();
        handleClose();
    };

    const canProceedStep0 = csvData !== null && csvData.rows.length > 0;
    const canProceedStep1 = mappings.every(m => !m.required || m.csvColumn !== '');
    const canProceedStep2 = getActiveUsers().length > 0 && getActiveValidationErrors().length === 0;
    const canSave = activeStep === 5
        && getActiveUsers().length > 0
        && getActiveValidationErrors().length === 0;
    const isResultsStep = activeStep === 6;

    const failedResults = importResult?.results.filter(r => !r.success) ?? [];
    const remainingFailedResults = failedResults.filter(r => !removedRows.has(submittedIndexes[r.rowNumber - 1]));
    const retryEnabled = canRetryImport(
        failedResults.map(r => r.rowNumber),
        submittedIndexes,
        removedRows,
        getActiveUsers().length
    );

    const getErrorsForRow = (rowIndex: number): RowValidationError[] => {
        return validationErrors.filter(e => e.rowIndex === rowIndex);
    };

    const getCellError = (rowIndex: number, field: string): string | undefined => {
        return validationErrors.find(e => e.rowIndex === rowIndex && e.field === field)?.message;
    };

    return (
        <React.Fragment>
            <StepperDialog
                handleCancel={handleCloseClicked}
                handleSave={isResultsStep ? handleCloseAfterImport : handleSave}
                handleNext={handleNext}
                handleBack={handleBack}
                handleCloseClicked={handleCloseClicked}
                title={title}
                open={open}
                hasPrevious={activeStep === 0 || (isResultsStep && (!importResult || importResult.failureCount === 0))}
                canSave={isResultsStep || canSave}
                saveLabel={isResultsStep ? 'Done' : undefined}
                stepper={
                    <Stepper activeStep={activeStep}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                }
            >
                <Box sx={{ width: 810, maxWidth: '100%' }} minHeight={300}>
                    {/* Step 1: Upload File */}
                    {activeStep === 0 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                {isInvite ? 'Upload a CSV file of email addresses. The first row must contain column headers.' : 'Upload a CSV file containing user data. The first row must contain column headers.'}
                            </Typography>

                            {fileName && (
                                <Box mb={2}>
                                    <TextField
                                        label="Selected File"
                                        value={fileName}
                                        fullWidth
                                        size="small"
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Box>
                            )}

                            <Box display="flex" justifyContent="center" my={3}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    sx={{
                                        py: 2.5, px: 5,
                                        '&:hover, &:focus': {
                                            background: 'rgba(225,227,230,0.5)',
                                            borderColor: '#E1E3E6',
                                            boxShadow: '0px 1px 3px rgba(11, 19, 36, 0.1), inset 0px -1px 0px rgba(0, 0, 0, 0.1)',
                                        },
                                    }}
                                    startIcon={<SelectFileIcon />}
                                >
                                    Select{fileName ? ' Another' : ' a'} CSV File
                                    <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
                                </Button>
                            </Box>

                            {fileError && (
                                <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>
                            )}

                            {csvData && (
                                <Box mt={2}>
                                    <Alert severity="success">
                                        Found {csvData.headers.length} columns and {csvData.rows.length} data row(s)
                                    </Alert>
                                    {csvData.errors.length > 0 && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            {csvData.errors.length} parsing warning(s): {csvData.errors.slice(0, 3).join('; ')}
                                            {csvData.errors.length > 3 && ` and ${csvData.errors.length - 3} more...`}
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 2: Map Fields */}
                    {activeStep === 1 && csvData && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Map CSV columns to user fields. Required fields are marked with *.
                            </Typography>

                            {mappings.map((mapping) => (
                                <Box key={mapping.systemField} display="flex" alignItems="center" mb={2} gap={2}>
                                    <Typography sx={{ minWidth: 140, fontWeight: 'medium' }}>
                                        {mapping.label}{mapping.required ? ' *' : ''}
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={mapping.csvColumn}
                                        onChange={(e) => handleMappingChange(mapping.systemField, e.target.value)}
                                        fullWidth
                                        label="CSV Column"
                                        error={mapping.required && !mapping.csvColumn}
                                        helperText={mapping.required && !mapping.csvColumn ? 'Required mapping' : ''}
                                    >
                                        <MenuItem value="">
                                            <em>-- Not Mapped --</em>
                                        </MenuItem>
                                        {csvData.headers.map((header) => (
                                            <MenuItem key={header} value={header}>
                                                {header}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Step 3: Review & Confirm */}
                    {activeStep === 2 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="body2" color="text.secondary">
                                    Review the imported data. Remove rows with errors before submitting.
                                </Typography>
                                <Box display="flex" gap={1}>
                                    <Chip
                                        label={`${getActiveUsers().length} user(s)`}
                                        color="primary"
                                        size="small"
                                    />
                                    {getActiveValidationErrors().length > 0 && (
                                        <Chip
                                            label={`${getActiveValidationErrors().length} error(s)`}
                                            color="error"
                                            size="small"
                                        />
                                    )}
                                </Box>
                            </Box>

                            {getActiveValidationErrors().length > 0 && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    Please fix or remove rows with errors before importing.
                                </Alert>
                            )}

                            <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
                                <StyledTable size="small" sx={{ tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '6%' }}>Row</TableCell>
                                            {!isInvite && <TableCell sx={{ width: '16%' }}>Username</TableCell>}
                                            <TableCell sx={{ width: isInvite ? '60%' : '24%' }}>Email</TableCell>
                                            {!isInvite && <TableCell sx={{ width: '13%' }}>First Name</TableCell>}
                                            {!isInvite && <TableCell sx={{ width: '13%' }}>Last Name</TableCell>}
                                            {!isInvite && <TableCell sx={{ width: '12%' }}>Phone</TableCell>}
                                            <TableCell sx={{ width: '10%' }} align="center">Status</TableCell>
                                            <TableCell sx={{ width: '6%' }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user, i) => {
                                            if (removedRows.has(i)) return null;
                                            const rowErrors = getErrorsForRow(i);
                                            const hasErrors = rowErrors.length > 0;
                                            return (
                                                <TableRow key={i} sx={hasErrors ? { backgroundColor: 'error.lighter' } : undefined}>
                                                    <TableCell>{i + 1}</TableCell>
                                                    {!isInvite &&
                                                        <TableCell>
                                                            <CellValue value={user.userName} error={getCellError(i, 'userName')} />
                                                        </TableCell>
                                                    }
                                                    <TableCell>
                                                        <CellValue value={user.email} error={getCellError(i, 'email')} />
                                                    </TableCell>
                                                    {!isInvite &&
                                                        <TableCell>
                                                            <CellValue value={user.firstName} error={getCellError(i, 'firstName')} />
                                                        </TableCell>
                                                    }
                                                    {!isInvite &&
                                                        <TableCell>
                                                            <CellValue value={user.lastName} error={getCellError(i, 'lastName')} />
                                                        </TableCell>
                                                    }
                                                    {!isInvite &&
                                                        <TableCell>
                                                            <CellValue value={user.phoneNumber} />
                                                        </TableCell>
                                                    }
                                                    <TableCell align="center">
                                                        {hasErrors ? (
                                                            <Tooltip title={rowErrors.map(e => e.message).join(', ')}>
                                                                <Chip label="Error" color="error" size="small" sx={{ color: '#fff' }} />
                                                            </Tooltip>
                                                        ) : (
                                                            <Chip label="OK" color="success" size="small" sx={{ color: '#fff' }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton size="small" onClick={() => handleRemoveRow(i)}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </StyledTable>
                            </Box>

                            <Box mt={1}>
                                {isInvite ? (
                                    <FormControlLabel
                                        control={<Checkbox checked={twoFactorEnabled} onChange={(_, checked) => setTwoFactorEnabled(checked)} />}
                                        label="Require two-factor authentication for the invited users"
                                    />
                                ) : (
                                    <FormControlLabel
                                        control={<Checkbox checked={sendWelcomeEmail} onChange={(_, checked) => setSendWelcomeEmail(checked)} />}
                                        label="Send welcome email to new users"
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Step 4: Applications */}
                    {activeStep === 3 && (
                        <BulkImportApplicationsPanel
                            clientIds={userClientIds}
                            onChange={setUserClientIds}
                        />
                    )}

                    {/* Step 5: Roles */}
                    {activeStep === 4 && (
                        <BulkImportRolesPanel
                            roleIds={roleIds}
                            onChange={setRoleIds}
                        />
                    )}

                    {/* Step 6: Organizations */}
                    {activeStep === 5 && (
                        <Box>
                            {importError && (
                                <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>
                            )}
                            <BulkImportOrganizationsPanel
                                organizations={userOrganizations}
                                onChange={setUserOrganizations}
                            />
                        </Box>
                    )}

                    {/* Step 7: Results */}
                    {activeStep === 6 && importResult && (
                        <Box>
                            {importResult.failureCount === 0 ? (
                                <Box>
                                    <Box display="flex" gap={2} mb={3} justifyContent="center">
                                        <Chip
                                            icon={<CheckCircleOutlineIcon sx={{ color: 'inherit' }} />}
                                            label={`${importResult.successCount} validated`}
                                            color="success"
                                            sx={{ color: '#fff' }}
                                        />
                                        <Chip label={`${importResult.totalRows} total`} variant="outlined" />
                                    </Box>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        {isInvite
                                            ? 'All addresses passed validation and have been queued for invitation. The users will appear in the users list shortly.'
                                            : 'All users passed validation and have been queued for creation. They will appear in the users list shortly.'}
                                    </Alert>
                                </Box>
                            ) : (
                                <Box>
                                    <Box display="flex" gap={2} mb={3} justifyContent="center">
                                        {importResult.successCount > 0 && (
                                            <Chip
                                                icon={<CheckCircleOutlineIcon sx={{ color: 'inherit' }} />}
                                                label={`${importResult.successCount} valid`}
                                                color="success"
                                                sx={{ color: '#fff' }}
                                            />
                                        )}
                                        <Chip
                                            icon={<ErrorOutlineIcon />}
                                            label={`${remainingFailedResults.length} failed validation`}
                                            color="error"
                                        />
                                        <Chip label={`${importResult.totalRows} total`} variant="outlined" />
                                    </Box>
                                    {retryEnabled ? (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            All failed rows removed — Retry Import will import
                                            the remaining {getActiveUsers().length} user(s).
                                        </Alert>
                                    ) : (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            Some {isInvite ? 'addresses' : 'users'} failed validation.
                                            No {isInvite ? 'invitations were sent' : 'users were imported'}.
                                            Remove the failed rows below to retry.
                                        </Alert>
                                    )}

                                    {remainingFailedResults.length > 0 && (
                                        <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
                                            <StyledTable size="small" sx={{ tableLayout: 'fixed' }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: '6%' }}>Row</TableCell>
                                                        {!isInvite && <TableCell sx={{ width: '18%' }}>Username</TableCell>}
                                                        <TableCell sx={{ width: isInvite ? '42%' : '24%' }}>Email</TableCell>
                                                        <TableCell sx={{ width: '10%' }} align="center">Status</TableCell>
                                                        <TableCell sx={{ width: '36%' }}>Details</TableCell>
                                                        <TableCell sx={{ width: '6%' }}></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {remainingFailedResults.map((row) => (
                                                        <TableRow key={row.rowNumber}>
                                                            <TableCell>{row.rowNumber}</TableCell>
                                                            {!isInvite &&
                                                                <TableCell>
                                                                    <CellValue value={row.userName} />
                                                                </TableCell>
                                                            }
                                                            <TableCell>
                                                                <CellValue value={row.email} />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Chip label="Failed" color="error" size="small" />
                                                            </TableCell>
                                                            <TableCell sx={{ wordBreak: 'break-word' }}>
                                                                {row.errors.length > 0 && (
                                                                    <Typography variant="body2" color="error">
                                                                        {row.errors.join(' ')}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Remove from import">
                                                                    <IconButton size="small" onClick={() => handleRemoveFailedRow(row.rowNumber)}>
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </StyledTable>
                                        </Box>
                                    )}

                                    <Box display="flex" justifyContent="flex-end" mt={2}>
                                        <Button
                                            variant="contained"
                                            disabled={!retryEnabled}
                                            onClick={handleSave}
                                        >
                                            {isInvite ? 'Retry Invite' : 'Retry Import'}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    <LoadingBackDrop open={loading} />
                </Box>
            </StepperDialog>

            <CloseConfirmationDialog
                open={closeDialogOpen}
                handleCancel={() => setCloseDialogOpen(false)}
                handleConfirm={handleCloseConfirmed}
            />
        </React.Fragment>
    );
};

const CellValue: React.FC<{ value: string; error?: string }> = ({ value, error }) => {
    const text = (
        <Typography
            variant="body2"
            noWrap
            color={error ? 'error' : undefined}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: error ? 'help' : undefined }}
        >
            {value || (error ? <em>empty</em> : '')}
        </Typography>
    );
    // Tooltip shows the error when present, otherwise the full (possibly truncated) value
    const title = error || value;
    return title ? <Tooltip title={title}>{text}</Tooltip> : text;
};

export default BulkImportUsersDialog;
